import { useState, useEffect } from 'react';
import api from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { FileText, Plus, Trash, PencilSimple, Copy, EnvelopeSimple, Notebook, Eye } from '@phosphor-icons/react';
import { WatchHowLink } from '../components/WatchHowLink';

const MERGE_FIELDS = [
  '{first_name}', '{last_name}', '{company}', '{email}', '{phone}',
  '{title}', '{salutation}', '{address_street}', '{address_city}',
  '{address_state}', '{address_zip}', '{address_country}', '{status}', '{date}',
];

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTpl, setEditingTpl] = useState(null);
  const [form, setForm] = useState({ name: '', subject: '', body: '', template_type: 'email' });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedMessage, setSeedMessage] = useState('');

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewContactSearch, setPreviewContactSearch] = useState('');
  const [previewContacts, setPreviewContacts] = useState([]);
  const [previewTplId, setPreviewTplId] = useState(null);

  const fetchTemplates = async () => {
    try {
      const { data } = await api.get('/templates');
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load templates', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const openCreate = () => {
    setEditingTpl(null);
    setForm({ name: '', subject: '', body: '', template_type: 'email' });
    setDialogOpen(true);
  };

  const openEdit = (tpl) => {
    setEditingTpl(tpl);
    setForm({ name: tpl.name, subject: tpl.subject || '', body: tpl.body, template_type: tpl.template_type || 'email' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.body.trim()) return;
    setSaving(true);
    try {
      if (editingTpl) {
        await api.put(`/templates/${editingTpl.template_id}`, form);
      } else {
        await api.post('/templates', form);
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (err) {
      console.error('Save failed', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tplId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/templates/${tplId}`);
      fetchTemplates();
    } catch {}
  };

  const insertMergeField = (field) => {
    setForm(f => ({ ...f, body: f.body + field }));
  };

  const handleSeedStarters = async () => {
    if (seeding) return;
    setSeeding(true);
    setSeedMessage('');
    try {
      const { data } = await api.post('/templates/seed-starters');
      const n = data?.created ?? 0;
      setSeedMessage(
        n > 0
          ? `Added ${n} starter template${n === 1 ? '' : 's'}.`
          : 'All starter templates are already installed.'
      );
      fetchTemplates();
    } catch (err) {
      console.error('Seed starters failed', err);
      setSeedMessage('Could not install starter templates. Try again.');
    } finally {
      setSeeding(false);
      setTimeout(() => setSeedMessage(''), 4000);
    }
  };

  // Preview / Merge
  const openPreview = (tplId) => {
    setPreviewTplId(tplId);
    setPreviewData(null);
    setPreviewContactSearch('');
    setPreviewContacts([]);
    setPreviewOpen(true);
  };

  const searchPreviewContacts = async (q) => {
    setPreviewContactSearch(q);
    if (q.length < 2) { setPreviewContacts([]); return; }
    try {
      const { data } = await api.get('/contacts/search/quick', { params: { q } });
      setPreviewContacts(data);
    } catch { setPreviewContacts([]); }
  };

  const mergePreview = async (contactId) => {
    try {
      const { data } = await api.post(`/templates/${previewTplId}/merge`, { contact_id: contactId });
      setPreviewData(data);
      setPreviewContacts([]);
      setPreviewContactSearch('');
    } catch (err) {
      console.error('Merge failed', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#0B5394] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="templates-page">
      {/* Toolbar */}
      <div className="bg-gradient-to-b from-[#E8E8E8] to-[#D0D0D0] border-b border-[#999] px-3 py-1.5 flex items-center gap-2 shrink-0">
        <FileText size={16} weight="bold" className="text-[#0B5394]" />
        <span className="text-[13px] font-bold text-[#333]">Templates</span>
        <WatchHowLink slug="templates" className="ml-1" />
        <div className="flex-1" />
        {seedMessage && (
          <span className="text-[10px] text-[#0B5394] mr-2" data-testid="seed-status">{seedMessage}</span>
        )}
        <button
          onClick={handleSeedStarters}
          disabled={seeding}
          className="act-toolbar-btn"
          title="Install 8 starter email templates (Quick Follow-Up, Meeting Confirmation, etc.)"
          data-testid="install-starters-btn"
        >
          <FileText size={14} weight="bold" />
          <span className="text-[10px]">{seeding ? 'Installing…' : '+ Starter Templates'}</span>
        </button>
        <button onClick={openCreate} className="act-toolbar-btn" data-testid="create-template-btn">
          <Plus size={14} weight="bold" />
          <span className="text-[10px]">New Template</span>
        </button>
      </div>

      <div className="flex-1 overflow-auto p-2 bg-[#E8E8E8]">
        {templates.length === 0 ? (
          <div className="bg-[#F5F5F0] border border-[#999] p-8 text-center">
            <FileText size={32} className="mx-auto mb-2 text-[#999]" />
            <p className="text-[12px] text-[#666] mb-3">No templates yet. Install our starter pack or write your own from scratch.</p>
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={handleSeedStarters}
                disabled={seeding}
                className="px-3 py-1.5 text-[11px] font-medium bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50"
                data-testid="empty-install-starters"
              >
                {seeding ? 'Installing…' : 'Install 8 Starter Templates'}
              </button>
              <button onClick={openCreate} className="px-3 py-1.5 text-[11px] font-medium border border-[#999] bg-white hover:bg-[#F0F0F0]" data-testid="empty-create-template">
                Create from Scratch
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {templates.map(tpl => (
              <div key={tpl.template_id} className="bg-[#F5F5F0] border border-[#999] flex flex-col" data-testid={`template-card-${tpl.template_id}`}>
                <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999] flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    {tpl.template_type === 'email' ? <EnvelopeSimple size={13} className="text-[#0B5394]" /> : <Notebook size={13} className="text-[#0B5394]" />}
                    <span className="text-[12px] font-bold text-[#333]">{tpl.name}</span>
                  </div>
                  <span className="text-[10px] text-[#999] capitalize">{tpl.template_type}</span>
                </div>
                <div className="p-3 flex-1">
                  {tpl.subject && <p className="text-[11px] text-[#666] mb-1"><b>Subject:</b> {tpl.subject}</p>}
                  <p className="text-[11px] text-[#333] whitespace-pre-wrap line-clamp-4">{tpl.body}</p>
                </div>
                <div className="px-3 py-2 border-t border-[#DDD] flex gap-2">
                  <button onClick={() => openEdit(tpl)} className="flex items-center gap-1 text-[10px] text-[#0B5394] hover:underline" data-testid={`edit-tpl-${tpl.template_id}`}>
                    <PencilSimple size={10} /> Edit
                  </button>
                  <button onClick={() => openPreview(tpl.template_id)} className="flex items-center gap-1 text-[10px] text-[#0B5394] hover:underline" data-testid={`preview-tpl-${tpl.template_id}`}>
                    <Eye size={10} /> Preview
                  </button>
                  <div className="flex-1" />
                  <button onClick={() => handleDelete(tpl.template_id)} className="flex items-center gap-1 text-[10px] text-[#C0392B] hover:underline" data-testid={`delete-tpl-${tpl.template_id}`}>
                    <Trash size={10} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Template Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] rounded-none border-[#999] p-0 gap-0" data-testid="template-dialog">
          <DialogHeader className="px-4 pt-3 pb-2 bg-[#E0E0E0] border-b border-[#999]">
            <DialogTitle className="text-[13px] font-bold text-[#333]">
              {editingTpl ? 'Edit Template' : 'New Template'}
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-3">
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-[11px] font-medium text-[#333]">Template Name</label>
                <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} autoFocus autoComplete="one-time-code"
                  className="w-full h-[28px] px-2 text-[12px] border border-[#B0B0B0] mt-1 focus:outline-none focus:border-[#0B5394]" data-testid="tpl-name-input" />
              </div>
              <div className="w-[100px]">
                <label className="text-[11px] font-medium text-[#333]">Type</label>
                <select value={form.template_type} onChange={(e) => setForm(f => ({ ...f, template_type: e.target.value }))}
                  className="w-full h-[28px] px-1 text-[12px] border border-[#B0B0B0] mt-1" data-testid="tpl-type-select">
                  <option value="email">Email</option>
                  <option value="letter">Letter</option>
                </select>
              </div>
            </div>
            {form.template_type === 'email' && (
              <div>
                <label className="text-[11px] font-medium text-[#333]">Subject</label>
                <input value={form.subject} onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))} autoComplete="one-time-code"
                  className="w-full h-[28px] px-2 text-[12px] border border-[#B0B0B0] mt-1 focus:outline-none focus:border-[#0B5394]" data-testid="tpl-subject-input"
                  placeholder="e.g. Follow up with {company}" />
              </div>
            )}
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-medium text-[#333]">Body</label>
                <span className="text-[10px] text-[#999]">Click merge fields to insert</span>
              </div>
              <div className="flex flex-wrap gap-1 my-1.5">
                {MERGE_FIELDS.map(field => (
                  <button key={field} onClick={() => insertMergeField(field)}
                    className="px-1.5 py-0.5 text-[10px] bg-[#0B5394] text-white hover:bg-[#073763] font-mono"
                    data-testid={`merge-field-${field}`}>
                    {field}
                  </button>
                ))}
              </div>
              <textarea value={form.body} onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))} rows={10}
                className="w-full px-2 py-1.5 text-[12px] border border-[#B0B0B0] focus:outline-none focus:border-[#0B5394] resize-none font-mono"
                placeholder={"Dear {salutation},\n\nThank you for your time today...\n\nBest regards"}
                data-testid="tpl-body-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2 px-4 py-2 bg-[#E0E0E0] border-t border-[#999]">
            <button onClick={() => setDialogOpen(false)} className="px-3 py-1 text-[11px] font-medium border border-[#999] bg-white hover:bg-[#F0F0F0]">Cancel</button>
            <button onClick={handleSave} disabled={saving || !form.name.trim() || !form.body.trim()}
              className="px-3 py-1 text-[11px] font-medium bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50" data-testid="save-template-btn">
              {saving ? '...' : 'Save Template'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview / Merge Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-[600px] rounded-none border-[#999] p-0 gap-0" data-testid="preview-dialog">
          <DialogHeader className="px-4 pt-3 pb-2 bg-[#E0E0E0] border-b border-[#999]">
            <DialogTitle className="text-[13px] font-bold text-[#333]">Preview Template</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            {!previewData ? (
              <div className="space-y-3">
                <p className="text-[12px] text-[#666]">Select a contact to merge with this template:</p>
                <input value={previewContactSearch} onChange={(e) => searchPreviewContacts(e.target.value)} placeholder="Search contact..."
                  autoFocus autoComplete="one-time-code"
                  className="w-full h-[28px] px-2 text-[12px] border border-[#B0B0B0] focus:outline-none focus:border-[#0B5394]" data-testid="preview-contact-search" />
                {previewContactSearch.length === 1 && (
                  <p className="text-[11px] text-[#888] italic" data-testid="preview-contact-hint">Keep typing — searching after 2 characters.</p>
                )}
                {previewContactSearch.length >= 2 && previewContacts.length === 0 && (
                  <p className="text-[11px] text-[#C0392B]" data-testid="preview-contact-no-results">
                    No contacts found matching "{previewContactSearch}". Try a shorter prefix, or check the Contacts list to confirm the name.
                  </p>
                )}
                {previewContacts.length > 0 && (
                  <div className="border border-[#B0B0B0] bg-white max-h-40 overflow-y-auto">
                    {previewContacts.map(c => (
                      <button key={c.contact_id} onClick={() => mergePreview(c.contact_id)}
                        className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-[#E8E8E8] border-b border-[#EEE]"
                        data-testid={`preview-contact-${c.contact_id}`}>
                        <span className="font-medium">{c.first_name} {c.last_name}</span>
                        {c.company && <span className="text-[#999] ml-1">({c.company})</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="mb-3 pb-2 border-b border-[#CCC]">
                  <span className="text-[10px] text-[#999]">Merged for: </span>
                  <span className="text-[12px] font-bold text-[#0B5394]">{previewData.contact_name}</span>
                </div>
                {previewData.subject && (
                  <div className="mb-2">
                    <span className="text-[10px] font-bold text-[#666]">Subject: </span>
                    <span className="text-[12px] text-[#333]">{previewData.subject}</span>
                  </div>
                )}
                <div className="bg-white border border-[#CCC] p-4 text-[12px] text-[#333] whitespace-pre-wrap min-h-[200px]" data-testid="preview-merged-body">
                  {previewData.body}
                </div>
                <button onClick={() => setPreviewData(null)} className="mt-2 text-[11px] text-[#0B5394] hover:underline">
                  Try another contact
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
